/* ============================================
   WikiSidebar — Navigation Sidebar for Wiki
   Search, categories, and article listing.
   ============================================ */

import { useState } from 'react'
import type { CategoriaConConteo, WikiArticulo } from '@/hooks/useWiki'

/* ── Inline SVG Icons ── */
const icons = {
  search: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  chevronRight: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  file: (s = 15) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  folder: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    </svg>
  ),
}

interface WikiSidebarProps {
  categorias: CategoriaConConteo[]
  articuloActual: WikiArticulo | null
  searchQuery: string
  onSearchChange: (query: string) => void
  onSelectArticulo: (catSlug: string, artSlug: string) => void
  onSelectCategoria: (catSlug: string) => void
}

export default function WikiSidebar({
  categorias,
  articuloActual,
  searchQuery,
  onSearchChange,
  onSelectArticulo,
  onSelectCategoria,
}: WikiSidebarProps) {
  const [expandedCats, setExpandedCats] = useState<Set<string>>(() => {
    // Auto-expand the category of the current article
    if (articuloActual?.categoria_id) {
      return new Set([articuloActual.categoria_id])
    }
    return new Set<string>()
  })

  const toggleCategory = (catId: string) => {
    setExpandedCats(prev => {
      const next = new Set(prev)
      if (next.has(catId)) {
        next.delete(catId)
      } else {
        next.add(catId)
      }
      return next
    })
  }

  const statusDot = (estado: string) => {
    const colors: Record<string, string> = {
      publicado: 'var(--success)',
      borrador: 'var(--warning)',
      archivado: 'var(--text-muted)',
    }
    return (
      <span
        className="wiki-sidebar__status-dot"
        style={{ background: colors[estado] || 'var(--text-muted)' }}
        title={estado}
      />
    )
  }

  return (
    <aside className="wiki-sidebar">
      {/* ── Search ── */}
      <div className="wiki-sidebar__search">
        <span className="wiki-sidebar__search-icon">{icons.search()}</span>
        <input
          type="text"
          className="wiki-sidebar__search-input"
          placeholder="Buscar artículos..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>

      {/* ── Categories List ── */}
      <nav className="wiki-sidebar__nav">
        {categorias.map(cat => {
          const isExpanded = expandedCats.has(cat.id)
          const hasArticles = cat.articulos.length > 0

          return (
            <div key={cat.id} className="wiki-sidebar__category">
              {/* Category Header */}
              <button
                className="wiki-sidebar__cat-header"
                onClick={() => {
                  if (hasArticles) {
                    toggleCategory(cat.id)
                  }
                  onSelectCategoria(cat.slug)
                }}
              >
                <span
                  className="wiki-sidebar__cat-dot"
                  style={{ background: cat.color || 'var(--primary-light)' }}
                />
                <span className="wiki-sidebar__cat-icon">
                  {icons.folder()}
                </span>
                <span className="wiki-sidebar__cat-name">{cat.nombre}</span>
                <span className="wiki-sidebar__cat-count">{cat.articulos_count}</span>
                {hasArticles && (
                  <span
                    className="wiki-sidebar__cat-chevron"
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    {icons.chevronRight()}
                  </span>
                )}
              </button>

              {/* Article List */}
              {isExpanded && hasArticles && (
                <div className="wiki-sidebar__articles">
                  {cat.articulos.map(art => {
                    const isActive =
                      articuloActual?.id === art.id

                    return (
                      <button
                        key={art.id}
                        className={`wiki-sidebar__article ${isActive ? 'wiki-sidebar__article--active' : ''}`}
                        onClick={() => onSelectArticulo(cat.slug, art.slug)}
                      >
                        <span className="wiki-sidebar__article-icon">{icons.file()}</span>
                        <span className="wiki-sidebar__article-title">{art.titulo}</span>
                        {statusDot(art.estado)}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

        {categorias.length === 0 && (
          <div className="wiki-sidebar__empty">
            No hay categorías creadas
          </div>
        )}
      </nav>
    </aside>
  )
}
